// Relative path: src/components/Loader.js
import React, { useEffect, useState, useRef } from 'react';
import SpinnerProgress from './SpinnerProgress';
import ReviewsTicker from './ReviewsTicker';

const Loader = ({ dataReady = false, onLoaderComplete }) => {
  const [progress, setProgress] = useState(0);
  const normalModeRef = useRef(true);
  const timerRef = useRef(null);

  useEffect(() => {
    const TICK_INTERVAL = 500;   // ms
    const FINAL_PAUSE = 1000;    // pause at 99% before going 100%
    const NORMAL_STEP = 0.58;    // slow increments up to ~95%
    const FAST_STEP = 23.47;     // faster jump after data is ready
    const PROGRESS_CAP = 95;     // do not exceed 95% if data is not ready

    const updateProgress = () => {
      setProgress((prev) => {
        let next = prev;

        // If data is ready and we are still in "normal" mode, switch to fast mode
        if (dataReady && normalModeRef.current) {
          normalModeRef.current = false;
        }

        // Decide step size
        const step = normalModeRef.current ? NORMAL_STEP : FAST_STEP;
        next = +(prev + step).toFixed(0);

        // If NOT ready, do not let progress exceed PROGRESS_CAP
        if (!dataReady && next >= PROGRESS_CAP) {
          return PROGRESS_CAP;
        }

        // If data is ready, proceed toward 99 -> final pause -> 100
        if (dataReady && next >= 99) {
          clearInterval(timerRef.current);
          setProgress(99);
          setTimeout(() => {
            setProgress(100);
            if (onLoaderComplete) {
              onLoaderComplete();
            }
          }, FINAL_PAUSE);
          return 99;
        }

        return next;
      });
    };

    // Start the interval
    timerRef.current = setInterval(updateProgress, TICK_INTERVAL);

    // Cleanup on unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [dataReady, onLoaderComplete]);

  // Ensure that when dataReady becomes true, we immediately switch to fast mode
  useEffect(() => {
    if (dataReady) {
      normalModeRef.current = false;
    }
  }, [dataReady]);

  const getStageText = (progress) => {
    // If data is ready, we are in finalizing mode
    if (!normalModeRef.current) {
      return 'Finalizing...';
    }

    // Normal progression text based on progress
    if (progress < 15) return 'Analyzing hundreds of reviews from all around the internet...';
    if (progress < 35) return 'Finding reviewers who are similar to you...';
    if (progress < 55) return 'Analyzing the experiences of similar reviewers...';
    if (progress < 65) return 'Evaluating the clinics based on the experiences of similar reviewers...';
    if (progress < 85) return 'Selecting clinics to recommend based on data...';
    if (progress < 95) return 'Generating evaluation report...';
    return 'Finalizing...';
  };

  return (
    <div className="loader-container" style={{ textAlign: 'center', padding: '20px' }}>
      <SpinnerProgress progress={progress} />
      <div className="stage-text" style={{ marginTop: '16px' }}>
        <p>{getStageText(progress)}</p>
      </div>
      <p className='font-extrabold text-mm_blue md:mt-12 mt-6 text-2xl'>Let's see what patients say!</p>
      <ReviewsTicker scrollSpeed={40} />
    </div>
  );
};

export default Loader;
